"use client"

import Text from "@/components/common/Typography/Text"
import Title from "@/components/common/Typography/Title"
import { Button, Col, Row } from "antd"
import { useEffect, useMemo, useState } from "react"
import { useAppSelector } from "@/lib/redux/hooks"
import { EnumEndpointsName } from "@/constants/app/type"
import { useTranslation } from "react-i18next"
import { useAirports } from "@/hooks/flight/useAirports"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import FlightDealsDesktop from "./FlightDealsDesktop"
import { getValidDayjs } from "@/utils/safeParse/getValidDayjs"
import dayjs from "dayjs"
import {
  getInternationalDestinationCountryCodes,
  INTERNATIONAL_DESTINATION_TABS,
} from "@/constants/flight/internationalDestinationTabs"

const getFlightCountryCode = (flight: any) => {
  return String(flight?.productMetaData?.countryCode || "").trim()
    || String(flight?.location || "").trim()
}

const FlightDeals = ({ flashSales, ...props }: any) => {
  const loading = useAppSelector(state => state.flight[EnumEndpointsName.GET_GOOD_FLIGHTS].loading)
  const [origin, setOrigin] = useState<any>("SGN")
  const { t } = useTranslation()
  const airports = useAirports()
  const { getAirportCityName } = useTETranslation()
  const [selectedDestinationKey, setSelectedDestinationKey] = useState<string>("")

  const flightsDomestic = flashSales.reduce((arr: any[], flashSale: any) => {
    return arr.concat(flashSale.items.filter((i: any) => {
      return i.productType === 'FLIGHT'
        && getValidDayjs(i.productMetaData.startDate).diff(dayjs(), 'minute') > 0
        && i.productMetaData.flightType === props.type
    }))
  }, [])?.sort((f1: any, f2: any) => (f1.flashPrice - f2.flashPrice))

  const isDomestic = props.type === "DOMESTIC"

  const destinationOptions = useMemo(() => {
    if (isDomestic) return []

    const presentCodes = new Set<string>()
    for (const flight of flightsDomestic) {
      const code = getFlightCountryCode(flight)
      if (code) presentCodes.add(code)
    }

    const predefinedKeys = new Set<string>()
    const options = INTERNATIONAL_DESTINATION_TABS
      .filter((tab) => tab.countryCodes.some((code) => presentCodes.has(code)))
      .map((tab) => {
        tab.countryCodes.forEach((code) => predefinedKeys.add(code))
        return { key: tab.key, label: tab.label, countryCodes: tab.countryCodes }
      })

    // Keep unexpected countries visible (append after fixed order)
    const extras = Array.from(presentCodes)
      .filter((code) => !predefinedKeys.has(code))
      .map((code) => {
        const airportEnd = Object.values(airports).find((ap: any) => {
          return String(ap.countryCode || "").trim() === code
        })
        return {
          key: code,
          label: String(airportEnd?.country || code),
          countryCodes: [code],
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label, "vi"))

    return [...options, ...extras]
  }, [airports, flightsDomestic, isDomestic])

  const originOptions = useMemo(() => {
    const codes: string[] = []
    for (const flight of flightsDomestic) {
      const code = flight?.productMetaData?.startPoint
      if (code && !codes.includes(code)) {
        codes.push(code)
      }
    }
    if (codes.includes("SGN")) {
      codes.splice(codes.indexOf("SGN"), 1)
      codes.unshift("SGN")
    }
    return codes.map((code) => ({
      key: code,
      label: getAirportCityName(airports[code]) || code,
    }))
  }, [airports, flightsDomestic, getAirportCityName])

  useEffect(() => {
    if (!flightsDomestic?.length) return
    const hasCurrentOrigin = flightsDomestic.some((f: any) => f.productMetaData?.startPoint === origin)
    if (hasCurrentOrigin) return
    const nextOrigin = flightsDomestic.some((f: any) => f.productMetaData?.startPoint === "SGN")
      ? "SGN"
      : flightsDomestic[0]?.productMetaData?.startPoint
    if (nextOrigin) setOrigin(nextOrigin)
  }, [flightsDomestic, origin])

  useEffect(() => {
    if (!destinationOptions.length) {
      if (selectedDestinationKey) setSelectedDestinationKey("")
      return
    }
    const stillValid = destinationOptions.some((opt) => opt.key === selectedDestinationKey)
    if (!stillValid) {
      setSelectedDestinationKey(destinationOptions[0].key)
    }
  }, [destinationOptions, selectedDestinationKey])

  const canShow = flightsDomestic.length > 0

  if (!canShow) {
    return <></>
  }

  const filteredFlights = flightsDomestic.filter((f: any) => {
    if (isDomestic || !selectedDestinationKey) {
      return true
    }
    const codes = getInternationalDestinationCountryCodes(selectedDestinationKey)
    const flightCode = getFlightCountryCode(f)
    return codes.includes(flightCode)
  })

  return (
    <Row {...props} style={{ marginBottom: '3rem', ...props.style }}>
      <Col span={24} style={{ marginBottom: '1rem' }}>
        {props.type === 'DOMESTIC' ? (
          <>
            <Title level={2} style={{ margin: 0 }}>
              {t("home.section_best_price_flights_title")}
            </Title>
            <div className="home__deals-flights--filter-row">
              <Text className="home__deals-flights--filter-label">
                {t("home.section_best_price_flights_from")}
              </Text>
              <div className="home__deals-flights--origin-tabs">
                {originOptions.map((opt) => (
                  <Button
                    key={opt.key}
                    size="small"
                    shape="round"
                    className="te-btn-round home__deals-flights--origin-tab"
                    type={origin === opt.key ? "primary" : "default"}
                    onClick={() => setOrigin(opt.key)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <Title
              level={2}
              style={{
                margin: 0,
              }}
            >
              {t("home.section_best_price_flights_title_international")}
            </Title>
            {!!destinationOptions.length && (
              <div className="home__deals-flights--filter-row">
                <Text className="home__deals-flights--filter-label">
                  {t("home.section_best_price_flights_to")}
                </Text>
                <div className="home__deals-flights--origin-tabs">
                  {destinationOptions.map((opt) => (
                    <Button
                      key={opt.key}
                      size="small"
                      shape="round"
                      className="te-btn-round home__deals-flights--origin-tab"
                      type={selectedDestinationKey === opt.key ? "primary" : "default"}
                      onClick={() => setSelectedDestinationKey(opt.key)}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

      </Col>
      <Col span={24}>
        <FlightDealsDesktop
          origin={isDomestic ? origin : ""}
          flights={filteredFlights}
          loading={loading}
          minWidth={992}
        />
        <FlightDealsDesktop
          key={isDomestic ? origin : `international-${selectedDestinationKey}`}
          origin={isDomestic ? origin : ""}
          flights={filteredFlights}
          loading={loading}
          singleSlide
          autoPlay={3000}
          maxWidth={991}
        />
      </Col>
    </Row>
  )
}

export default FlightDeals
